import { useAppDispatch } from '../../../providers';
import { RESOURCE_QUEUE_SELECTED_ID, SELECTION_LIST_SELECTED_ID } from '../../../providers/AppProvider/types';

import { Button } from '@pella/sash-framework';

import { VALUE_DISPLAY_CONSTANTS } from './ValueDisplayConstants';

import * as classes from './ChildSetups.module.css';

const CONFIG = {
  'resources-setup-cards': { label: 'Queues', objectKey: 'queue' },
  'queues-setup-cards': { label: 'Resources', objectKey: 'resource' },
  'selectionLists-setup-cards': { label: 'Values', objectKey: 'values' },
  'actions-setup-cards': { label: 'Queue', objectKey: 'actionQueue' },
};

/**
 * ChildSetups Molecule Component
 * @param {Local.ChildSetupsProps} props - Component props
 */
export const ChildSetups = ({ id, data, manageChildren }) => {
  const { resourceQueues, values, queue } = data;

  const dispatch = useAppDispatch();

  const { label, objectKey } = CONFIG[id] || {};

  const calculateVisibleTags = (maxRows = 4) => {
    const CARD_WIDTH = VALUE_DISPLAY_CONSTANTS.cardWidth;
    const GAP_SIZE = VALUE_DISPLAY_CONSTANTS.gapSize;
    const BADGE_H_PADDING = VALUE_DISPLAY_CONSTANTS.badgeHPadding;
    const PLUS_BADGE_WIDTH = VALUE_DISPLAY_CONSTANTS.plusBadgeWidth;
    const CHAR_WIDTH = VALUE_DISPLAY_CONSTANTS.charWidth;

    const tags = values || [];
    if (!tags.length) return tags.length;

    let currentWidth = 0;
    let currentRow = 1;
    let visibleCount = 0;

    for (let i = 0; i < tags.length; i++) {
      // Calculate tag width (truncate at 25 chars to match rendering)
      const displayText =
        tags[i].description.length > 25 ? `${tags[i].description.slice(0, 25)}...` : tags[i].description;
      const tagWidth = Math.ceil(displayText.length * CHAR_WIDTH + BADGE_H_PADDING);

      // Add gap width if not first item in row
      const gapWidth = currentWidth > 0 ? GAP_SIZE : 0;
      const totalNeeded = currentWidth + gapWidth + tagWidth;

      // Check if we need to wrap to next row
      if (totalNeeded > CARD_WIDTH && currentWidth > 0) {
        currentRow++;
        currentWidth = tagWidth;
        // Stop if we've exceeded max rows
        if (currentRow > maxRows) {
          break;
        }
      } else {
        currentWidth = totalNeeded;
      }

      // On the last row, reserve space for "+N" badge if more items remain
      if (currentRow === maxRows && i < tags.length - 1) {
        const spaceNeededForBadge = currentWidth + GAP_SIZE + PLUS_BADGE_WIDTH;
        if (spaceNeededForBadge > CARD_WIDTH) {
          // Don't include this item, save space for badge
          break;
        }
      }

      visibleCount++;
    }

    return visibleCount;
  };

  const visibleCount = calculateVisibleTags();
  const visibleTags = values?.slice(0, visibleCount);
  const hiddenCount = values?.length ? values.length - visibleCount : 0;

  if (!label || !objectKey) return null;

  /**
   * handles dynamic selection for resources and queues and then also selection list values
   */
  const handleExtraButtonClick = () => {
    if (label === 'Resources' || label === 'Queues') {
      dispatch({ type: RESOURCE_QUEUE_SELECTED_ID, payload: data.id });
    } else if (label === 'Values') {
      dispatch({ type: SELECTION_LIST_SELECTED_ID, payload: data.id });
    }
  };

  // check to see if we should render actions based on the values passed into this component
  const willRenderActions = resourceQueues || values || queue;

  return (
    willRenderActions && (
      <div className={classes.container}>
        <div className={classes.label}>
          <div>{label}</div>
          {manageChildren && (
            <div>
              <Button
                className={classes.iconButton}
                color='tertiary'
                iconLeft='assignment'
                onClick={handleExtraButtonClick}
                aria-label={`Manage ${label}`}
              />
            </div>
          )}
          {queue && (
            <div className={classes.itemContainer}>
              <div className={`${classes.item} ${classes.active}`} key={queue.id}>
                {queue.description}
              </div>
            </div>
          )}
        </div>

        {resourceQueues && (
          <div className={classes.itemContainer}>
            {resourceQueues?.map((assignment) => (
              <div
                className={`${classes.item} ${assignment.active ? classes.active : classes.inactive}`}
                key={assignment[objectKey].id}
              >
                {assignment[objectKey].description}
              </div>
            ))}
          </div>
        )}
        {values && (
          <div className={classes.itemContainer}>
            {visibleTags?.map((assignment) => (
              <div className={`${classes.item} ${classes.active}`} key={assignment.id}>
                {assignment.description.length > 25
                  ? `${assignment.description.slice(0, 25)}...`
                  : assignment.description}
              </div>
            ))}
            {hiddenCount > 0 && (
              <div className={`${classes.item} ${classes.badge}`} aria-label={`${hiddenCount} more values`}>
                +{hiddenCount}
              </div>
            )}
          </div>
        )}
      </div>
    )
  );
};
